import { ref, reactive, computed, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { useSessionStore } from '@/stores/SessionStore'
import useStorageSystem from '@/composables/useStorageSystem';

import useConfigurationGlobals from "@/composables/useConfigurationGlobals";
import useUserGlobals from '@/composables/useUserGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { 
  $ACCESS_LEVEL, 
  $CAN_ADS_TOGGLE, 
  $DEFAULT_ADS_TOGGLE_STATE, 
  $MEMBER_LEVEL, 
  $MEMBER_LEVEL_NAME, 
  $NON_TRIAL_MEMBER_LEVEL, 
  $NON_TRIAL_MEMBER_LEVEL_NAME, 
  $SHOW_ADS, 
  $SHOW_ADS_TOGGLE, 
  $SHOW_NC_WIND_TOGGLE, 
  $WF_USERNAME, 
  $MESSAGES, 
  $FIRST_TIME_USER, 
  $USER_SIGNED_IN,
  $SETTINGS
} = useUserGlobals();

export let useUserStore = defineStore('userStore', () => {
    const sessionStore = useSessionStore();

    const accessLevel = ref($ACCESS_LEVEL);
    const canAdsToggle = ref($CAN_ADS_TOGGLE)
    const defaultAdsToggleState = ref($DEFAULT_ADS_TOGGLE_STATE)
    const [lastSelectedProfileId] = useStorageSystem(null, 'lastSelectedProfileId', 'localStorage');
    const memberLevel = ref($MEMBER_LEVEL)
    const memberLevelName = ref($MEMBER_LEVEL_NAME)
    const nonTrialMemberLevel = ref($NON_TRIAL_MEMBER_LEVEL)
    const nonTrialMemberLevelName = ref($NON_TRIAL_MEMBER_LEVEL_NAME)
    const showAds = ref($SHOW_ADS ? $SHOW_ADS.toLowerCase() !== 'false' : true)
    const showAdsToggle = ref($SHOW_ADS_TOGGLE)
    const showNCWindToggle = ref($SHOW_NC_WIND_TOGGLE)
    const userName = ref($WF_USERNAME)
    const userProfiles = ref(null);
    const currentProfile = ref(0);
    const userSignedIn = ref($USER_SIGNED_IN ? $USER_SIGNED_IN.toLowerCase() !== 'false' : true)
    const firstTimeUser = ref($FIRST_TIME_USER);
    const messages = ref(null);

    const apiStorage = ref(null);
    const loadingAPIStorage = ref(false);

    const favoriteSpotData = ref(null);
    const favoriteSortOrder = ref(localStorage.getItem('FavSortOrder') || 'windspeed');

    const currentProfileId = computed(() => currentProfile.value?.profile_id);
    const profileContainers = computed(() => currentProfile.value?.containers.filter(container => container.container_type_id.toLowerCase() !== 'map'));
    const profileMaps = computed(() => currentProfile.value?.containers.filter(container => container.container_type_id.toLowerCase() === 'map'));

    const units = reactive({
      wind: $SETTINGS.units_wind,
      temp: $SETTINGS.units_temp,
      precip: $SETTINGS.units_precip,
      distance: $SETTINGS.units_distance
    })

    try {
      messages.value = [];

      if ($MESSAGES && $MESSAGES !== '') {
        messages.value = JSON.parse($MESSAGES);
      }
    } catch(error) {
      console.warn('Could not parse user messages: ', error);
    }

    // TODO: Move fetch into composable
    async function getProfiles(refresh = false) {
      if (userProfiles.value != null && !refresh) return userProfiles.value;

      try {
        const response = await fetch(
          `${$BASEURL}/profile/getProfiles?wf_token=${sessionStore.token}&units_wind=mph&units_temp=f&units_distance=mi&units_precip=in`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json();

        if (data.status.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        data.profiles.forEach(profile => {
          const filteredContainers = profile.containers.filter(container => container.container_type_id.toLowerCase() !== 'favorite spots');
          profile.containers = filteredContainers;
        })

        userProfiles.value = data;

        if (!currentProfileId.value && !lastSelectedProfileId.value) {
          setDefaultProfile();
        } else if (lastSelectedProfileId.value) {
          setCurrentProfile(lastSelectedProfileId.value);
        } else {
          setCurrentProfile(currentProfileId.value)
        }

        return userProfiles.value
      } catch (error) {
        console.warn(error)
      }           
    }

    async function addSpotToProfile(profileId, spotId, { name, lat, lon }) {
      const params = {
        profile_id: profileId,
        spot_id: spotId,
        name: name,
        lat: !spotId ? lat : null,
        lon: !spotId ? lon : null,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/addSpotToProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function removeSpotFromProfile(profileId, favSpotId, refreshSpotData = false) {
      try {
        const response = await fetch( `${$BASEURL}/profile/removeSpotFromProfile?profile_id=${profileId}&fav_spot_id=${favSpotId}&wf_token=${sessionStore.token}` )

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json();

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        if (refreshSpotData) {
          getSpotDetailSetByProfile();
        }

        return data;
      } catch (error) {
        console.warn(error);
      }  
    }

    async function updateProfileContainer(containerId, name) {

      const params = {
        name,
        container_id: containerId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/updateProfileContainer?${queryString}`
        )

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }        

        return data;
      } catch (error) {
        console.warn(error)
      }
    }

    async function addContainerToProfile(profileId, containerTypeId, name, settings) {

      const params = {
        profile_id: profileId,
        container_type_id: containerTypeId,
        options: JSON.stringify(settings),
        name: name,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/addContainerToProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
          return
        }

        return data;
      } catch (error) {
        console.warn(error)
      }

    }

    async function removeContainerFromProfile(containerId) {

      const params = {
        container_id: containerId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/removeContainerFromProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        return data;
      } catch (error) {
        console.warn(error)
      }

    }

    async function getAPIStorage() {
      if (memberLevel.value < 0) return;

      loadingAPIStorage.value = true;

      const params = {
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString();

      try {
        const response = await fetch(`${$BASEURL}/profile/getUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        setApiStorage(data);
        loadingAPIStorage.value = false;
        
        return data
      } catch (error) {
        loadingAPIStorage.value = false;
        console.warn(error);
      }
    }    

    async function updateAPIStorageItem(key, value) {
      if (memberLevel.value < 0) return;

      const params = {
        wf_token: sessionStore.token,
        name: key,
        value
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/updateUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function deleteAPIStorage() {
      if (memberLevel.value < 0) return;

      const params = {
        wf_token: sessionStore.token,
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/deleteUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function getSpotDetailSetByProfile() {
      if (!currentProfile.value.profile_id) return [];

      const params = {
        units_wind: units.wind,
        units_temp: units.temp,
        units_distance: units.distance,
        units_precip: units.precip,
        spot_types: [1, 100, 101].join(','),
        stormprint_only: false,
        sort: favoriteSortOrder.value,
        profile_id: currentProfile.value.profile_id,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/spot/getSpotDetailSetByProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status
          return
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
          return
        }

        favoriteSpotData.value = data;

        return favoriteSpotData.value
      } catch (error) {
        console.warn(error)
      }
    }

    async function getAPIStorageItem(key) {
      if (!apiStorage.value) {
        
        if (loadingAPIStorage.value) {
          // Wait until loading is complete
          while (loadingAPIStorage.value) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } else {
          await getAPIStorage();
        }
      };
      
      return apiStorage.value ? apiStorage.value[key] : null;
    }

    // checkToken() for testing only
    async function checkToken() {
      try {
        const response = await fetch(
          `${$BASEURL}/session/checkToken?wf_token=${sessionStore.token}`
        )
  
        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json();

        if (data.status.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }
        
        return data;
      } catch(error) {
        console.warn(error);
      }      
    }

    function setUser(settings) {
      accessLevel.value = settings.access_level;
      canAdsToggle.value = settings.can_ads_toggle;
      defaultAdsToggleState.value = settings.default_ads_toggle_state;
      memberLevel.value = settings.member_level;
      memberLevelName.value = settings.member_level_name;
      nonTrialMemberLevel.value = settings.non_trial_member_level;
      nonTrialMemberLevelName.value = settings.non_trial_member_level_name;
      showAds.value = settings.show_ads;
      showAdsToggle.value = settings.show_ads_toggle;
      userSignedIn.value = parseInt(settings.non_trial_member_level) === -1;
      userName.value = settings.wf_username;
    }

    function setCurrentProfile(profileId) {
      if (currentProfileId.value === profileId) {
        currentProfile.value = getProfileById(profileId);
        return currentProfile.value;
      }

      if (!profileId) {
        if (lastSelectedProfileId.value) {
          const profile = getProfileById(lastSelectedProfileId.value);

          if (profile) {
            currentProfile.value = profile;
          } else {
            setDefaultProfile();
          }
        } else {
          setDefaultProfile();
        }

        return currentProfile.value;
      }

      const foundProfile = getProfileById(profileId);

      if (!foundProfile) {
        console.warn(`Profile with ID ${profileId} not found.`);

        setDefaultProfile();
        lastSelectedProfileId.value = currentProfileId.value;
      } else {
        currentProfile.value = foundProfile;
        lastSelectedProfileId.value = profileId;
      }

      return currentProfile.value;
    }

    function refreshCurrentProfile() {
      if (!currentProfileId.value) return null;

      const foundProfile = getProfileById(currentProfileId.value);

      if (!foundProfile) {
        console.warn(`Profile with ID ${currentProfileId.value} not found.`);

        return null;
      } else {
        currentProfile.value = foundProfile;
      }

      return currentProfile.value;
    }

    function setDefaultProfile() {
      currentProfile.value = userProfiles.value.profiles.find(profile => profile.master === 'true');

      return currentProfile.value;
    }

    function getProfileById(profileId) {
      return userProfiles.value.profiles.find(profile => profile.profile_id === profileId);
    }

    function setApiStorage(data) {
      delete data.status;

      apiStorage.value = data;
    }

    function isSpotFavoritedInCurrentProfile({spotId, lat = null, lon = null}) {
      const profile = getProfileById(currentProfileId.value);
      let spotFavorited = null

      for (let favoriteSpot of profile.favorite_spots) {
        if (
          !favoriteSpot.spot_id &&
          favoriteSpot.lat === lat &&
          favoriteSpot.lon === lon
        ) {
          spotFavorited = [currentProfileId.value, favoriteSpot.fav_spot_id]
          break
        }

        if (
          favoriteSpot.spot_id &&
          spotId &&
          favoriteSpot.spot_id === spotId
        ) {
          spotFavorited = [currentProfileId.value, favoriteSpot.fav_spot_id]
          break
        }
      }

      return spotFavorited
    }

    watchEffect(() => {
      window.$EventBus.dispatchEvent('user-profile-changed', {
        profileId: currentProfile.value?.profile_id
      });
    })

    return {
      apiStorage,
      currentProfile,
      currentProfileId,
      favoriteSpotData,
      memberLevel,
      memberLevelName,
      messages,
      nonTrialMemberLevel,
      nonTrialMemberLevelName,
      profileContainers,
      profileMaps,
      showAds,
      showNCWindToggle,
      units,
      userName,
      userProfiles,
      userSignedIn,
      addContainerToProfile,
      addSpotToProfile,
      checkToken,
      deleteAPIStorage,
      getAPIStorage,
      getAPIStorageItem,
      getProfileById,
      getProfiles,
      getSpotDetailSetByProfile,
      isSpotFavoritedInCurrentProfile,
      refreshCurrentProfile,
      removeContainerFromProfile,
      removeSpotFromProfile,
      setCurrentProfile,
      setDefaultProfile,
      setUser,
      updateAPIStorageItem,
      updateProfileContainer,
    }
});
